import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';

/**
 * Удобный выбор месяца. Чип со стрелочками ‹/›, клик по тексту открывает
 * popup с годом и сеткой 4×3 месяцев.
 *
 *   <MonthPicker value="2026-04" onChange={(v) => …} />
 */
const MONTH_FULL  = ['Январь','Февраль','Март','Апрель','Май','Июнь',
                     'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const MONTH_SHORT = ['Янв','Фев','Мар','Апр','Май','Июн',
                     'Июл','Авг','Сен','Окт','Ноя','Дек'];

function pad2(n) { return String(n).padStart(2, '0'); }
function parseValue(v) {
  // 'YYYY-MM' → { year, month (1..12) }. Если кривое — текущий месяц.
  if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v)) {
    return { year: parseInt(v.slice(0, 4), 10), month: parseInt(v.slice(5, 7), 10) };
  }
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}
function buildValue(y, m) { return y + '-' + pad2(m); }

export default function MonthPicker({ value, onChange }) {
  const cur = parseValue(value);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(cur.year);
  const ref = useRef(null);

  // Сбрасываем view-год к выбранному при открытии
  useEffect(() => { if (open) setViewYear(cur.year); /* eslint-disable-line */ }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;

  const shift = (delta) => {
    let m = cur.month + delta;
    let y = cur.year;
    while (m < 1)  { m += 12; y--; }
    while (m > 12) { m -= 12; y++; }
    onChange(buildValue(y, m));
  };

  const pickMonth = (m) => {
    onChange(buildValue(viewYear, m));
    setOpen(false);
  };

  const summary = MONTH_FULL[cur.month - 1] + ' ' + cur.year;

  return (
    <div className="month-picker" ref={ref}>
      <button
        type="button"
        className="month-picker__nav"
        onClick={() => shift(-1)}
        title="Предыдущий месяц"
      >‹</button>

      <button
        type="button"
        className={'chip month-picker__chip' + (open ? ' is-open' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="calendar" size={12} />
        <span className="chip__value">{summary}</span>
        <Icon name="chevronDown" size={10} />
      </button>

      <button
        type="button"
        className="month-picker__nav"
        onClick={() => shift(1)}
        title="Следующий месяц"
      >›</button>

      {open && (
        <div className="month-picker__pop">
          <div className="month-picker__yearbar">
            <button type="button" className="month-picker__nav" onClick={() => setViewYear((y) => y - 1)}>‹</button>
            <span className="month-picker__year">{viewYear}</span>
            <button type="button" className="month-picker__nav" onClick={() => setViewYear((y) => y + 1)}>›</button>
          </div>
          <div className="month-picker__grid">
            {MONTH_SHORT.map((label, i) => {
              const m = i + 1;
              const isSelected = viewYear === cur.year && m === cur.month;
              const isToday    = viewYear === todayY && m === todayM;
              return (
                <button
                  key={m}
                  type="button"
                  className={
                    'month-picker__cell'
                    + (isSelected ? ' is-selected' : '')
                    + (isToday    ? ' is-today'    : '')
                  }
                  onClick={() => pickMonth(m)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="month-picker__quick">
            <button type="button" className="month-picker__quickbtn"
              onClick={() => { onChange(buildValue(todayY, todayM)); setOpen(false); }}>
              Этот месяц
            </button>
            <button type="button" className="month-picker__quickbtn"
              onClick={() => {
                let m = todayM - 1, y = todayY;
                if (m < 1) { m = 12; y--; }
                onChange(buildValue(y, m)); setOpen(false);
              }}>
              Прошлый месяц
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
